import { useNavigate, useParams } from 'react-router-dom'

import { Box, Card, Text } from '@factorialco/deprecated-design-system'
import {
  DetailsItemType,
  DetailsItemsList,
  RichTextDisplay,
} from '@factorialco/f0-react/dist/experimental'
import {
  AtsRequisitionDetailPageDocument,
  AtsRequisitionJobCatalogNodesDocument,
} from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { FORMAT, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { RequisitionStatusTag } from 'modules/ats/components/RequisitionStatusTag'

import { JobRequisitionDetailPageSkeleton } from './JobRequisitionDetailPageSkeleton'
import { RequestActions } from './RequestActions'

const JobRequisitionDetailPage = () => {
  const params = useParams()
  const requisitionId = Number(params.requisition_id)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { data } = useQuery(AtsRequisitionDetailPageDocument, {
    variables: { id: requisitionId },
  })

  const requisition = data?.ats.requisition || undefined

  const roleUuid = requisition?.jobCatalogNodeRolesUuid
  const levelUuid = requisition?.jobCatalogNodeLevelsUuid

  const { data: jobCatalogData, loading: jobCatalogLoading } = useQuery(
    AtsRequisitionJobCatalogNodesDocument,
    {
      variables: {
        roleUuid: roleUuid ?? '',
        levelUuid: levelUuid ?? '',
      },
      skip: !roleUuid && !levelUuid,
    }
  )

  const roleName = jobCatalogData?.jobCatalog.roleNode.nodes[0]?.name
  const levelName = jobCatalogData?.jobCatalog.levelNode.nodes[0]?.name

  const {
    access: { id: currentAccessId },
    company,
  } = useCurrentSession()

  const showFooterActions = () => {
    if (!requisition) {
      return false
    }
    const isApprover = requisition.currentApproverIds?.includes(currentAccessId)
    const isAuthorOrApprover = requisition.author.id == currentAccessId || isApprover

    return requisition.status === 'pending' && isAuthorOrApprover
  }

  const onClose = () => {
    navigate(navigation.ats.jobs.requisitions.fullPath())
  }

  const items: DetailsItemType[] = []
  const additionalData: Array<{ id: string; label: string; value: string }> = []
  const formatSalary = (valueInCents: number) =>
    i18n.formatNumber(valueInCents / 100, 'currency', company.currency ?? undefined)

  if (requisition) {
    items.push({
      content: {
        text: requisition.jobPostingTitle,
        type: 'item',
      },
      title: i18n.t('ats.jobs_requisitions.requisition_detail.job_opening_title'),
    })
    if (requisition.team)
      items.push({
        content: [
          {
            action: {
              type: 'navigate',
              href: navigation.employees.teams.id.fullPath({ id: requisition.team.id }),
            },
            type: 'team',
            name: requisition.team.name,
          },
        ],
        title: i18n.t('ats.jobs_requisitions.requisition_detail.team'),
      })

    items.push(
      {
        content: {
          text: requisition.workplace?.city ?? '',
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.workplace'),
      },

      {
        content: {
          text:
            requisition.reason === 'new_position'
              ? i18n.t('ats.jobs_requisitions.requisition_detail.reasons.new_position')
              : i18n.t('ats.jobs_requisitions.requisition_detail.reasons.backfill'),
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.reason'),
      }
    )
    if (requisition.hiringManager)
      items.push({
        content: {
          action: {
            type: 'navigate',
            href: navigation.employees.id.fullPath({ id: requisition.hiringManager.id }),
          },
          type: 'person',
          avatarUrl: buildUrl(requisition.hiringManager.avatar ?? ''),
          firstName: requisition.hiringManager.fullName ?? '',
          lastName: '',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.hiring_manager'),
      })

    if (requisition.recruiter)
      items.push({
        content: {
          action: {
            type: 'navigate',
            href: navigation.employees.id.fullPath({ id: requisition.recruiter.id }),
          },
          type: 'person',
          avatarUrl: buildUrl(requisition.recruiter.avatar ?? ''),
          firstName: requisition.recruiter.fullName ?? '',
          lastName: '',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.process_owner'),
      })

    if (requisition.numberOfPositions)
      items.push({
        content: {
          text: String(requisition.numberOfPositions),
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.number_of_vacancies'),
      })

    if (requisition.startDate)
      items.push({
        content: {
          text: stringToDate(requisition.startDate, 'backend').format(FORMAT.ui),
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.start_date'),
      })

    if (requisition.salaryFrom)
      items.push({
        content: {
          text: formatSalary(requisition.salaryFrom),
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.salary_from'),
      })

    if (requisition.salaryTo)
      items.push({
        content: {
          text: formatSalary(requisition.salaryTo),
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.salary_to'),
      })

    if (requisition.salaryPeriod)
      items.push({
        content: {
          text: requisition.salaryPeriod,
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.salary_period'),
      })

    if (requisition.contractType)
      items.push({
        content: {
          text: requisition.contractType,
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.contract_type'),
      })

    if (requisition.scheduleType)
      items.push({
        content: {
          text: requisition.scheduleType,
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.schedule_type'),
      })

    if (roleName)
      items.push({
        content: {
          text: roleName,
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.role'),
      })

    if (levelName)
      items.push({
        content: {
          text: levelName,
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.level'),
      })

    if (requisition.status === 'rejected' && requisition.materializedApprovalFlow?.reason) {
      additionalData.push({
        id: 'rejection_reason',
        label: i18n.t('ats.jobs_requisitions.requisition_detail.rejection_reason'),
        value: requisition.materializedApprovalFlow.reason,
      })
    }
    if (requisition.jobPosting)
      items.push({
        content: {
          action: {
            type: 'navigate',
            href: navigation.ats.jobs.jobsId.fullPath({ jobId: requisition.jobPosting.id }),
          },
          type: 'item',
          text: requisition.jobPosting.title ?? '',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.job_posting'),
      })
  }

  return (
    <Sidepanel
      title={
        <Box alignItems="center" justifyContent="center" flexDirection="row" flexGrow gap="s12">
          <Text.Normal weight="semibold">
            {i18n.t('ats.jobs_requisitions.requisition_detail.title')}
          </Text.Normal>
          {!!requisition?.status && <RequisitionStatusTag status={requisition.status} />}
        </Box>
      }
      onClose={onClose}
      width="wide"
      closeLabel={i18n.t('ats.jobs_requisitions.requisition_detail.close')}
      footer={showFooterActions() && requisition && <RequestActions requisition={requisition} />}
      boxPadding
      open
    >
      <Box height="full">
        {requisition && !jobCatalogLoading ? (
          <Box flexGrow gap="s8">
            <Box paddingBottom="s24">
              <ApprovalsInformationBlock
                resourceId={requisitionId}
                resourceType="Ats::Requisition"
              />
            </Box>
            <DetailsItemsList
              details={items}
              title={i18n.t('ats.jobs_requisitions.requisition_detail.details')}
              tableView
            />
            {requisition.note && (
              <Card>
                <Card.Content>
                  <Box gap="s12" flexDirection="column">
                    <Box width="s140">
                      <Text.Normal size="200">
                        {i18n.t('ats.jobs_requisitions.requisition_detail.note')}
                      </Text.Normal>
                    </Box>
                    <RichTextDisplay content={requisition.note} />
                  </Box>
                </Card.Content>
              </Card>
            )}
            {additionalData.map((item) => (
              <Card key={item.id}>
                <Card.Content>
                  <Box gap="s12" flexDirection="column" key={item.id}>
                    <Box width="s140">
                      <Text.Normal size="200">{item.label}</Text.Normal>
                    </Box>
                    <Text.Normal weight="medium" size="200" color="grey1000">
                      {item.value}
                    </Text.Normal>
                  </Box>
                </Card.Content>
              </Card>
            ))}
          </Box>
        ) : (
          <JobRequisitionDetailPageSkeleton />
        )}
      </Box>
    </Sidepanel>
  )
}

export default JobRequisitionDetailPage
